// ReportsTab orchestrator. Composes hooks and renders tab header + sub-tab routing.
// See hooks/, insights/, survey/, question/, data/, assist/ for extracted modules.

import React from "react";
import { useSelector } from "react-redux";
import { userSelector } from "store/user";
import { useReportsData } from "./hooks/use-reports-data";
import { useReportsFilters } from "./hooks/use-reports-filters";
import { useReportDownload } from "./hooks/use-report-download";
import { useReportsDerivedData } from "./hooks/use-reports-derived-data";
import {
    Button,
    TabPanel,
    Typography,
    Card,
    CardBody,
    Tabs,
    Tab,
    TabsHeader,
    TabsBody,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem,
} from "@material-tailwind/react";
import InfoDialog from "components/dialog/info-dialog";
import MaterialIcon from "components/material-icon";
import { InsightsTab } from "./insights/insights-tab";
import { SurveyTab } from "./survey/survey-tab";
import { QuestionTab } from "./question/question-tab";
import { DownloadsTab } from "./data/downloads-tab";
import { AssistTab } from "./assist/assist-tab";

export default function ReportsTab({
    tabValue,
    survey,
    surveyId,
    surveyQuestions,
    surveyAbuse,
    setShowRespondentResponseSentimentListDialog,
    setResponseSentimentListId,
    respondentSentimentData,
    questionSentimentIndex,
    setQuestionSentimentIndex,
    setShowResponsesSentimentListDialog,
    setResponseSentimentIdList,
    respondentSentimentPage,
    setRespondentSentimentPage,
}) {

    // Get user data to check if main admin (team members should not see credit warnings)
    const { userData } = useSelector(userSelector);

    const {
        rawSurveyReport,
        questionResponsesSentiment,
        questionTotalSentimentData,
        sentimentByKeyphrase,
        keyphraseSentiment,
        setKeyphraseSentiment,
        keyphrasesPage,
        setKeyphrasesPage,
        questionTotalSentimentPage,
        setQuestionTotalSentimentPage,
    } = useReportsData({ survey, surveyId });

    const {
        activeTab,
        setActiveTab,
        selectedQuestionId,
        setSelectedQuestionId,
        openChartTables,
        setOpenChartTables,
        legendDirection,
        setLegendDirection,
        getLegendPosition,
        getBottomMargin,
        getBottomMarginNoLegend,
        getLegendItemWidth,
    } = useReportsFilters();

    const {
        pieDemographicData,
        completionData,
        surveyTimePeriod,
        questionDetails,
        totalResponses,
        totalUnThemed,
        totalVotes,
        totalModeratedResponses,
        topThemesForSurvey,
        allResponsesByThemePerQ,
        topThemesForEachQuestion,
        questionEngagementData,
        themeEngagementData,
        perQuestionData,
        themeByContributors,
        themeByContributorsForChart,
        themeByResponses,
        themeByResponsesForChart,
        themeByUnique,
        themeByUniqueForChart,
        themeByTotalEngagement,
        themeByTotalEngagementForChart,
        questionByContributors,
        questionByContributorsForChart,
        questionByResponses,
        questionByResponsesForChart,
        questionByUnique,
        questionByUniqueForChart,
        questionByTotalEngagement,
        questionByTotalEngagementForChart,
    } = useReportsDerivedData({
        rawSurveyReport,
        survey,
        surveyQuestions,
        surveyAbuse,
        selectedQuestionId,
        setSelectedQuestionId,
    });

    const toggleChartTable = (key) => {
        setOpenChartTables(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const { csvDownloadHandler } = useReportDownload({ survey, surveyId });


    // Only show unpaid-response banner to main admins when data is loaded.
    const hasUnpaidResponses = userData && survey && userData.is_main_admin && (survey.credit_state === "UNPAID" || survey.credit_state === "HIGH_UNPAID");

    return (
        <>
            <TabPanel value={tabValue} className="">
                <div>
                    {!!hasUnpaidResponses && (
                        <Card className="mb-4 bg-orange-50 border-l-4 border-orange-400 p-3">
                            <div className="flex items-center gap-2">
                                <MaterialIcon name="info" className="text-orange-500" size="sm" />
                                <Typography variant="small" className="text-orange-800">
                                    Reports only include paid responses. Some responses may be excluded awaiting credit purchase.
                                </Typography>
                            </div>
                        </Card>
                    )}
                    <Card className="card p-0">
                        <CardBody className="card-inner-body">
                            <Tabs value={activeTab} key={activeTab} className="w-full">
                                <div className="flex justify-end items-center mb-2">
                                    <InfoDialog title="Using the Survey Report">
                                        <Typography
                                            variant="paragraph"
                                            className="text-primary"
                                        >
                                        The Reports section of your survey is split
                                        into several sections that will give you a
                                        greater understanding of some of the data
                                        that has been collected. It is not a
                                        comprehensive analysis but a starting point
                                        for a deeper understanding.
                                            <br />
                                            <br />
                                        The charts are live from the moment the
                                        survey is launched and will update
                                        automatically. If you have enabled Amazon
                                        Comprehend to help with your manual Theming
                                        in Theme Manager it will automatically
                                        update as the survey is completed by your
                                        survey recipients. Don&apos;t worry if you
                                        started your survey without enabling Amazon
                                        Comprehend&apos;s natural language
                                        processing as you can send your survey to
                                        this service after you have closed it.
                                            <br />
                                            <br />
                                        Note that uncompleted surveys and partially
                                        completed questions are normally added to
                                        some insights and charts, especially for
                                        processing using ML and NLP at the end of
                                        the survey once it has been closed.
                                        </Typography>
                                        <div>
                                            <ul className="list-disc pl-6 space-y-4 text-primary">
                                                <li>
                                                    <span className="font-bold">
                                                    Insights
                                                    </span>{" "}
                                                is a quick overview of the most
                                                pertinent information we can
                                                determine from your survey. This is
                                                presented in a narrative format with
                                                some automatically derived
                                                statistics.
                                                </li>
                                                <li>
                                                    <span className="font-bold">
                                                    Charts
                                                    </span>{" "}
                                                are split into &lsquo;General
                                                Data&rsquo;, &lsquo;Themes
                                                Data&rsquo; and &lsquo;Demographic
                                                Data&rsquo;. Charts are live views
                                                of the data collected in your survey
                                                results. Use the selection box at
                                                the top of the &lsquo;Demographic
                                                Data&rsquo; section to change which
                                                question you see the results for
                                                (note that this only applies to
                                                surveys with multiple questions that
                                                are marked as demographic data).
                                                </li>
                                                <li>
                                                    <span className="font-bold">
                                                    Data downloads
                                                    </span>{" "}
                                                are the raw data available in a
                                                selection of output variations. This
                                                data is a direct snapshot of what is
                                                in the survey database at the time
                                                of pressing the button. All data is
                                                collated and presented in CSV
                                                format.
                                                </li>
                                                <li>
                                                The sections on{" "}
                                                    <span className="font-bold">
                                                    &lsquo;Sentiment Analysis&rsquo;
                                                    </span>
                                                ,{" "}
                                                    <span className="font-bold">
                                                    &lsquo;Respondents
                                                    Sentiment&rsquo;
                                                    </span>
                                                ,{" "}
                                                    <span className="font-bold">
                                                    &lsquo;Keyphrases and Keyphrase
                                                    Sentiment&rsquo;
                                                    </span>{" "}
                                                and{" "}
                                                    <span className="font-bold">
                                                    &lsquo;Sentiment by
                                                    Question&rsquo;
                                                    </span>{" "}
                                                display information returned when
                                                Amazon Comprehend has been selected
                                                in the survey settings, or when the
                                                data is sent to Amazon Comprehend
                                                when a survey is completed. This
                                                data provides insight into the
                                                sentiment of the language used, as
                                                well as extracting keyphrases.
                                                    <br />
                                                The sentiment is given a score to
                                                indicate the percentage of
                                                confidence (the higher the
                                                percentage, the more likely the
                                                sentiment is accurate). Further
                                                breakdowns show the sentiment
                                                displayed by an individual survey
                                                respondent and the number of each
                                                type of sentiment used by question,
                                                offering some insight into the
                                                general sentiment bias of each.
                                                </li>
                                            </ul>
                                        </div>
                                    </InfoDialog>
                                </div>
                                <div className="hidden md:block">
                                    <TabsHeader className="mb-2">
                                        <Tab value="insights" className="tab" onClick={() => setActiveTab("insights")}>
                                        Insights
                                        </Tab>
                                        <Tab value="survey" className="tab" onClick={() => setActiveTab("survey")}>
                                        Survey
                                        </Tab>
                                        <Tab value="questions" className="tab" onClick={() => setActiveTab("questions")}>
                                        Question
                                        </Tab>
                                        <Tab value="data" className="tab" onClick={() => setActiveTab("data")}>
                                        Downloads
                                        </Tab>
                                        <Tab value="assist" className="tab" onClick={() => setActiveTab("assist")}>
                                            Assist
                                        </Tab>
                                    </TabsHeader>
                                </div>
                                <div className="md:hidden mb-1">
                                    <Menu>
                                        <MenuHandler>
                                            <Button className="w-full justify-start gap-4 btn btn--primary">
                                                <MaterialIcon size="xl" name="menu" className="mr-2" />
                                                Report Sections
                                            </Button>
                                        </MenuHandler>
                                        <MenuList>
                                            <MenuItem onClick={() => setActiveTab("insights")}>Insights</MenuItem>
                                            <MenuItem onClick={() => setActiveTab("survey")}>Survey</MenuItem>
                                            <MenuItem onClick={() => setActiveTab("questions")}>Question</MenuItem>
                                            <MenuItem onClick={() => setActiveTab("data")}>Downloads</MenuItem>
                                            <MenuItem onClick={() => setActiveTab("assist")}>Assist</MenuItem>
                                        </MenuList>
                                    </Menu>
                                </div>
                                <TabsBody>
                                    <TabPanel value="insights">
                                        <InsightsTab
                                            surveyTimePeriod={surveyTimePeriod}
                                            completionData={completionData}
                                            totalResponses={totalResponses}
                                            totalVotes={totalVotes}
                                            totalModeratedResponses={totalModeratedResponses}
                                            totalUnThemed={totalUnThemed}
                                            questionDetails={questionDetails}
                                            topThemesForSurvey={topThemesForSurvey}
                                            allResponsesByThemePerQ={allResponsesByThemePerQ}
                                            topThemesForEachQuestion={topThemesForEachQuestion}
                                        />
                                    </TabPanel>
                                    <TabPanel value="survey">
                                        <SurveyTab
                                            totalResponses={totalResponses}
                                            completionData={completionData}
                                            pieDemographicData={pieDemographicData}
                                            questionEngagementData={questionEngagementData}
                                            questionByTotalEngagement={questionByTotalEngagement}
                                            questionByTotalEngagementForChart={questionByTotalEngagementForChart}
                                            questionByContributors={questionByContributors}
                                            questionByContributorsForChart={questionByContributorsForChart}
                                            questionByResponses={questionByResponses}
                                            questionByResponsesForChart={questionByResponsesForChart}
                                            questionByUnique={questionByUnique}
                                            questionByUniqueForChart={questionByUniqueForChart}
                                            themeEngagementData={themeEngagementData}
                                            themeByTotalEngagement={themeByTotalEngagement}
                                            themeByTotalEngagementForChart={themeByTotalEngagementForChart}
                                            themeByContributors={themeByContributors}
                                            themeByContributorsForChart={themeByContributorsForChart}
                                            themeByResponses={themeByResponses}
                                            themeByResponsesForChart={themeByResponsesForChart}
                                            themeByUnique={themeByUnique}
                                            themeByUniqueForChart={themeByUniqueForChart}
                                            openChartTables={openChartTables}
                                            toggleChartTable={toggleChartTable}
                                            legendDirection={legendDirection}
                                            getLegendPosition={getLegendPosition}
                                            getBottomMargin={getBottomMargin}
                                            getBottomMarginNoLegend={getBottomMarginNoLegend}
                                            getLegendItemWidth={getLegendItemWidth}
                                        />
                                    </TabPanel>
                                    <TabPanel value="questions">
                                        <QuestionTab
                                            perQuestionData={perQuestionData}
                                            selectedQuestionId={selectedQuestionId}
                                            setSelectedQuestionId={setSelectedQuestionId}
                                            openChartTables={openChartTables}
                                            toggleChartTable={toggleChartTable}
                                            legendDirection={legendDirection}
                                            getLegendPosition={getLegendPosition}
                                            getBottomMargin={getBottomMargin}
                                            getBottomMarginNoLegend={getBottomMarginNoLegend}
                                            getLegendItemWidth={getLegendItemWidth}
                                        />
                                    </TabPanel>
                                    <TabPanel value="data">
                                        <DownloadsTab csvDownloadHandler={csvDownloadHandler} />
                                    </TabPanel>
                                    <TabPanel value="assist">
                                        <AssistTab
                                            questionTotalSentimentData={questionTotalSentimentData}
                                            questionTotalSentimentPage={questionTotalSentimentPage}
                                            setQuestionTotalSentimentPage={setQuestionTotalSentimentPage}
                                            sentimentByKeyphrase={sentimentByKeyphrase}
                                            keyphraseSentiment={keyphraseSentiment}
                                            setKeyphraseSentiment={setKeyphraseSentiment}
                                            keyphrasesPage={keyphrasesPage}
                                            setKeyphrasesPage={setKeyphrasesPage}
                                            questionResponsesSentiment={questionResponsesSentiment}
                                            questionSentimentIndex={questionSentimentIndex}
                                            setQuestionSentimentIndex={setQuestionSentimentIndex}
                                            respondentSentimentData={respondentSentimentData}
                                            respondentSentimentPage={respondentSentimentPage}
                                            setRespondentSentimentPage={setRespondentSentimentPage}
                                            setResponseSentimentIdList={setResponseSentimentIdList}
                                            setShowResponsesSentimentListDialog={setShowResponsesSentimentListDialog}
                                            setResponseSentimentListId={setResponseSentimentListId}
                                            setShowRespondentResponseSentimentListDialog={setShowRespondentResponseSentimentListDialog}
                                        />
                                    </TabPanel>
                                </TabsBody>
                            </Tabs>
                        </CardBody>
                    </Card>
                </div>
            </TabPanel>
        </>
    );
}
